import { FC } from "react";
import Button from "@mui/material/Button";
import { SegmentDialog } from "./SegmentDialog";
import { ConfirmationDialog } from "@/components/Dialog/ConfirmationDialog";
import { Segment } from "@/apis/bacchus/types";

interface DialogState {
  isOpen: boolean;
  data: Segment | undefined;
  close: () => void;
}

export interface SegmentModificationDialogsProps {
  /** Delete dialog state from useDialog<Segment>() */
  deleteDialog: DialogState;

  /** Edit dialog state from useDialog<Segment>() */
  editDialog: DialogState;

  /** Delete confirmation handler */
  onDelete: () => void;

  /** Edit save handler */
  onEditSave: () => void;

  /** Whether delete is in progress */
  isDeleting?: boolean;

  /** Optional: Segment name to display in delete message (fallback to deleteDialog.data?.name) */
  segmentName?: string;
}

/**
 * Renders the segment modification dialogs (Edit + Delete).
 *
 * This component handles dialogs for modifying existing segments.
 * For creating new segments, use SegmentDialog with mode="create" separately.
 *
 * This is a presentational component that extracts common dialog JSX.
 * All logic (handlers, state management) stays in the parent component.
 *
 * @example
 * const deleteDialog = useDialog<Segment>();
 * const editDialog = useDialog<Segment>();
 *
 * <SegmentModificationDialogs
 *   deleteDialog={deleteDialog}
 *   editDialog={editDialog}
 *   onDelete={handleConfirmDelete}
 *   onEditSave={handleEditSave}
 *   isDeleting={isDeleting}
 * />
 */
export const SegmentModificationDialogs: FC<
  SegmentModificationDialogsProps
> = ({
  deleteDialog,
  editDialog,
  onDelete,
  onEditSave,
  isDeleting = false,
  segmentName,
}) => {
  const displayName = segmentName || deleteDialog.data?.name;

  return (
    <>
      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialog.isOpen}
        title="Delete Segment"
        description={
          displayName
            ? `Are you sure you want to delete "${displayName}"? This action cannot be undone.`
            : "Are you sure you want to delete this segment? This action cannot be undone."
        }
        cancelButtonText="Cancel"
        okayButtonText={
          <Button
            variant="contained"
            color="error"
            onClick={onDelete}
            disabled={isDeleting}
          >
            {isDeleting ? "Deleting..." : "Delete"}
          </Button>
        }
        onClose={deleteDialog.close}
      />

      {/* Edit Segment Dialog */}
      <SegmentDialog
        open={editDialog.isOpen}
        mode="edit"
        segment={editDialog.data}
        onClose={editDialog.close}
        onSave={onEditSave}
      />
    </>
  );
};
